import React, { useEffect, useState, Suspense, lazy } from 'react';
import { LanguageProvider } from './contexts/LanguageContext';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { Mission, MissionPanels } from './components/Mission';
import { ConsularBanner } from './components/ConsularBanner';
import { UpcomingEventsScroller } from './components/UpcomingEventsScroller';
import { LandingPage } from './components/LandingPage';
import { FoundingFundersPopup } from './components/FoundingFundersPopup';
import { GoogleAnalytics } from './components/GoogleAnalytics';
import { useLanguage } from './contexts/LanguageContext';
// Lazy-loaded below-the-fold sections
const CommunityStrength = lazy(() => import('./components/CommunityStrength').then(m => ({
  default: m.CommunityStrength
})));
const Leadership = lazy(() => import('./components/Leadership').then(m => ({
  default: m.Leadership
})));
const AdvisoryCouncil = lazy(() => import('./components/AdvisoryCouncil').then(m => ({
  default: m.AdvisoryCouncil
})));
const MakeDifferenceCTA = lazy(() => import('./components/MakeDifferenceCTA').then(m => ({
  default: m.MakeDifferenceCTA
})));
const NewsBlog = lazy(() => import('./components/NewsBlog').then(m => ({
  default: m.NewsBlog
})));
const MediaScroller = lazy(() => import('./components/MediaScroller').then(m => ({
  default: m.MediaScroller
})));
const ConsularAffairs = lazy(() => import('./components/ConsularAffairs').then(m => ({
  default: m.ConsularAffairs
})));
const BusinessInvestments = lazy(() => import('./components/BusinessInvestments').then(m => ({
  default: m.BusinessInvestments
})));
const OurPeopleStories = lazy(() => import('./components/OurPeopleStories').then(m => ({
  default: m.OurPeopleStories
})));
const NonprofitDirectory = lazy(() => import('./components/NonprofitDirectory').then(m => ({
  default: m.NonprofitDirectory
})));
const ExploreHonduras = lazy(() => import('./components/ExploreHonduras').then(m => ({
  default: m.ExploreHonduras
})));
const HondurasQuiz = lazy(() => import('./components/HondurasQuiz').then(m => ({
  default: m.HondurasQuiz
})));
const FAQ = lazy(() => import('./components/FAQ').then(m => ({
  default: m.FAQ
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const SocialMediaVoices = lazy(() => import('./components/SocialMediaVoices').then(m => ({
  default: m.SocialMediaVoices
})));
const ChatbaseWidget = lazy(() => import('./components/ChatbaseWidget').then(m => ({
  default: m.ChatbaseWidget
})));
// Lazy-loaded sub-pages
const AdminDashboard = lazy(() => import('./components/AdminDashboard').then(m => ({
  default: m.AdminDashboard
})));
const ConsularPage = lazy(() => import('./pages/ConsularPage').then(m => ({
  default: m.ConsularPage
})));
const BusinessMapPage = lazy(() => import('./pages/BusinessMapPage').then(m => ({
  default: m.BusinessMapPage
})));
const MemberProfilePage = lazy(() => import('./pages/MemberProfilePage').then(m => ({
  default: m.MemberProfilePage
})));
const NewsStoryPage = lazy(() => import('./pages/NewsStoryPage').then(m => ({
  default: m.NewsStoryPage
})));
const MembersDirectoryPage = lazy(() => import('./pages/MembersDirectoryPage').then(m => ({
  default: m.MembersDirectoryPage
})));
const SaveTheDatePage = lazy(() => import('./pages/SaveTheDatePage').then(m => ({
  default: m.SaveTheDatePage
})));
const NationalNetworkPage = lazy(() => import('./pages/NationalNetworkPage').then(m => ({
  default: m.NationalNetworkPage
})));
const EncuentroCatrachoMiamiPage = lazy(() => import('./pages/EncuentroCatrachoMiamiPage').then(m => ({
  default: m.EncuentroCatrachoMiamiPage
})));
const VoicesOfImpactNYPage = lazy(() => import('./pages/VoicesOfImpactNYPage').then(m => ({
  default: m.VoicesOfImpactNYPage
})));
const HonduranIndependenceDayPage = lazy(() => import('./pages/HonduranIndependenceDayPage').then(m => ({
  default: m.HonduranIndependenceDayPage
})));
const VoicesOfImpactNetworkingPage = lazy(() => import('./pages/VoicesOfImpactNetworkingPage').then(m => ({
  default: m.VoicesOfImpactNetworkingPage
})));
// Lightweight loading placeholder — subtle pulse instead of spinner to reduce visual jank
function SectionLoader() {
  return <div className="w-full py-16 flex items-center justify-center" data-id="element-0" data-mp="14xg6t0bzi0v4">
      <div className="flex items-center gap-2" data-id="element-1" data-mp="zx29ac80e9bc">
        <div className="w-2 h-2 rounded-full bg-haa-blue/30 animate-pulse" data-id="element-2" data-mp="zzwtq3r0vu4r" />
        <div className="w-2 h-2 rounded-full bg-haa-blue/20 animate-pulse" style={{
        animationDelay: '150ms'
      }} data-id="element-3" data-mp="vvp32110843w" />
        <div className="w-2 h-2 rounded-full bg-haa-blue/10 animate-pulse" style={{
        animationDelay: '300ms'
      }} data-id="element-4" data-mp="3gly07vv9sgb5" />
      </div>
    </div>;
}
function AppContent({
  showLanding,
  setShowLanding
}: {
  showLanding: boolean;
  setShowLanding: (v: boolean) => void;
}) {
  const {
    setLanguage
  } = useLanguage();
  const [pendingScroll, setPendingScroll] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const hash = window.location.hash;
    return isSectionAnchor(hash) ? resolveAnchor(hash.slice(1)) : null;
  });
  const handleSelectLanguage = (lang: 'EN' | 'ES') => {
    setLanguage(lang);
    setShowLanding(false);
  };
  const handleConsularAccess = (lang: 'EN' | 'ES') => {
    setLanguage(lang);
    setShowLanding(false);
    setPendingScroll('consular-affairs');
  };
  // React to in-page hash changes (e.g. opening a /#leadership link while already on the site)
  useEffect(() => {
    const handler = () => {
      const hash = window.location.hash;
      if (isSectionAnchor(hash)) {
        setShowLanding(false);
        setPendingScroll(resolveAnchor(hash.slice(1)));
      }
    };
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, [setShowLanding]);
  useEffect(() => {
    if (!showLanding && pendingScroll) {
      // Lazy-loaded sections may not be mounted yet — retry briefly until the element exists.
      const start = Date.now();
      let cancelled = false;
      const tryScroll = () => {
        if (cancelled) return;
        const el = document.getElementById(pendingScroll);
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth'
          });
          setPendingScroll(null);
          return;
        }
        if (Date.now() - start < 4000) {
          setTimeout(tryScroll, 150);
        } else {
          setPendingScroll(null);
        }
      };
      const timer = setTimeout(tryScroll, 100);
      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }
  }, [showLanding, pendingScroll]);
  if (showLanding) {
    return <LandingPage onSelectLanguage={handleSelectLanguage} onConsularAccess={handleConsularAccess} data-id="element-5" data-mp="3i1jw9chzhnoc" />;
  }
  return <div className="font-nunito w-full min-h-screen overflow-x-hidden" data-id="element-6" data-mp="385yr55hmc7ad">
      <Navbar data-id="element-7" data-mp="387teeqgd4bhm" />
      <FoundingFundersPopup data-id="element-8" data-mp="3d49ztcw6gbr0" />
      <main data-id="element-9" data-mp="3d3igp1jro6hm">
        <Hero data-id="element-10" data-mp="3d2yuv9qzwxyb" />
        <UpcomingEventsScroller data-id="element-11" data-mp="3d2p7ltxc54wb" />
        <ConsularBanner data-id="element-12" data-mp="2zdex6ic9uqvv" />
        <Mission data-id="element-13" data-mp="2zc3vqp6atv7x" />

        {/* Each section loads independently for faster perceived performance */}
        <Suspense fallback={<SectionLoader data-id="element-15" data-mp="345er9u5nupkj" />} data-id="element-14" data-mp="344wyj7i7ithd">
          <OurPeopleStories data-id="element-16" data-mp="345olxv1nhkwk" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-18" data-mp="2pgin486q5wp7" />} data-id="element-17" data-mp="2pg0jgqhqgg79">
          <MakeDifferenceCTA data-id="element-19" data-mp="2pd4isuei3df4" />
        </Suspense>
        <div className="max-w-5xl mx-auto px-4" data-id="element-20" data-mp="2uf3pad5jeqga">
          <div className="h-px bg-gradient-to-r from-transparent via-haa-blue/20 to-transparent" data-id="element-21" data-mp="2ubpjsw1ku14m" />
        </div>
        <Suspense fallback={<SectionLoader data-id="element-23" data-mp="2kge3waefmnva" />} data-id="element-22" data-mp="2kfuvplel3dug">
          <NewsBlog data-id="element-24" data-mp="2kekz70ndqdpx" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-26" data-mp="30v3vmqe1h010" />} data-id="element-25" data-mp="2khpbklb00fro">
          <MediaScroller data-id="element-27" data-mp="30uc53tzatshl" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-29" data-mp="2vwk6kh45f84f" />} data-id="element-28" data-mp="30t139zpeyut4">
          <CommunityStrength data-id="element-30" data-mp="2vxc3bv9hfbwa" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-32" data-mp="2qwxtq8s8ftw5" />} data-id="element-31" data-mp="2vug4m4pt8j6s">
          <MissionPanels data-id="element-33" data-mp="2qw68nrw9hek4" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-35" data-mp="2lyd2gyuykkju" />} data-id="element-34" data-mp="2qz0sbh7gavch">
          <Leadership data-id="element-36" data-mp="2lx31a0st6w8z" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-38" data-mp="2hwj632rsq2ph" />} data-id="element-37" data-mp="2m07bbesygm7k">
          <AdvisoryCouncil data-id="element-39" data-mp="2hwrgmo0g4vxi" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-41" data-mp="2d5sz5l3q1rso" />} data-id="element-40" data-mp="2hym3w8z6x04r">
          <MakeDifferenceCTA data-id="element-42" data-mp="2d5j7lt313ckv" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-44" data-mp="286ptpzjtxjk6" />} data-id="element-43" data-mp="2d3pze9crokar">
          <BusinessInvestments data-id="element-45" data-mp="285wwbp6rg6ep" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-47" data-mp="237n15el1d5t3" />} data-id="element-46" data-mp="284lsxqh890o4">
          <ConsularAffairs data-id="element-48" data-mp="238eknr1czd30" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-50" data-mp="1y8iq0iejwy04" />} data-id="element-49" data-mp="235imc1llmme1">
          <SocialMediaVoices data-id="element-51" data-mp="1y8s5vd5vtos3" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-53" data-mp="1u6x4y9rv5deu" />} data-id="element-52" data-mp="1yamr6sl2fiwp">
          <NonprofitDirectory data-id="element-54" data-mp="1u6oueoj7qk6t" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-56" data-mp="9oweiciss8mj" />} data-id="element-55" data-mp="1u91jdlne1utd">
          <HondurasQuiz data-id="element-57" data-mp="9pny0oz4efwg" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-59" data-mp="dqhnvnw4i4rz" />} data-id="element-58" data-mp="9qz3ct87rvpo">
          <ExploreHonduras data-id="element-60" data-mp="dpq3l97z7th0" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-62" data-mp="dsc78xrqxoty" />} data-id="element-61" data-mp="dooiebx8xqlk">
          <FAQ data-id="element-63" data-mp="ip2m5hrteeds" />
        </Suspense>
      </main>
      <Suspense fallback={null} data-id="element-64" data-mp="iqdqbip69o5f">
        <Footer data-id="element-65" data-mp="nofbc965rxjj" />
      </Suspense>
      <Suspense fallback={null} data-id="element-66" data-mp="nnmdjxp6gidj">
        <ChatbaseWidget data-id="element-67" data-mp="nml0jnolplt6" />
      </Suspense>
    </div>;
}
// Short, shareable aliases that resolve to the actual section IDs in the DOM.
// Example: /#council scrolls to the <section id="advisory-council">.
const SECTION_ALIASES: Record<string, string> = {
  council: 'advisory-council',
  members: 'our-stories',
  consular: 'consular-affairs',
  business: 'business-investments',
  nonprofit: 'nonprofit-directory',
  explore: 'explore-honduras',
  trivia: 'honduras-trivia',
  socials: 'social-voices'
};
// Section anchors that, when present in the URL hash, should bypass the landing page
// and scroll directly to that section on the main site. Both canonical IDs and the
// friendly aliases above are accepted.
const SECTION_ANCHORS = new Set(['home', 'mission', 'community', 'leadership', 'advisory-council', 'our-stories', 'news', 'social-voices', 'explore-honduras', 'honduras-trivia', 'nonprofit-directory', 'business-investments', 'consular-affairs', 'faq', 'contact', ...Object.keys(SECTION_ALIASES)]);
function resolveAnchor(id: string): string {
  return SECTION_ALIASES[id] ?? id;
}
function isSectionAnchor(hash: string): boolean {
  if (!hash || !hash.startsWith('#')) return false;
  if (hash.startsWith('#/')) return false;
  const id = hash.slice(1);
  return SECTION_ANCHORS.has(id);
}
export function App() {
  const initialHash = typeof window !== 'undefined' ? window.location.hash : '';
  // The site opens directly on the English main page; language is switched from
  // the toggle in the navigation bar rather than a separate entry screen.
  const [showLanding, setShowLanding] = useState(false);
  const [showAdmin, setShowAdmin] = useState(() => window.location.hash === '#/admin');
  const [showConsular, setShowConsular] = useState(() => window.location.hash === '#/consular');
  const [showBusinessMap, setShowBusinessMap] = useState(() => window.location.hash.startsWith('#/business-map'));
  const [businessMapLang, setBusinessMapLang] = useState<'EN' | 'ES'>(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/business-map')) return 'EN';
    const match = hash.match(/[?&]lang=(EN|ES)/);
    return match ? match[1] as 'EN' | 'ES' : 'EN';
  });
  const [showMembers, setShowMembers] = useState(() => window.location.hash === '#/members');
  const [showSaveTheDate, setShowSaveTheDate] = useState(() => window.location.hash === '#/save-the-date');
  const [showVoicesNY, setShowVoicesNY] = useState(() => window.location.hash === '#/voices-of-impact-ny');
  const [showVoicesNetworking, setShowVoicesNetworking] = useState(() => window.location.hash === '#/voices-of-impact-ny-networking');
  const [showEncuentroMiami, setShowEncuentroMiami] = useState(() => window.location.hash === '#/encuentro-catracho-miami');
  const [showIndependenceDay, setShowIndependenceDay] = useState(() => window.location.hash === '#/independence-day');
  const [showNationalNetwork, setShowNationalNetwork] = useState(() => window.location.hash === '#/national-network');
  const [showMember, setShowMember] = useState(() => window.location.hash.startsWith('#/member/'));
  const [memberSlug, setMemberSlug] = useState(() => {
    const hash = window.location.hash;
    return hash.startsWith('#/member/') ? hash.replace('#/member/', '') : '';
  });
  const [showNews, setShowNews] = useState(() => window.location.hash.startsWith('#/news/'));
  const [newsPostId, setNewsPostId] = useState(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/news/')) return '';
    const raw = hash.replace('#/news/', '');
    return raw.split('?')[0];
  });
  const [newsLang, setNewsLang] = useState<'EN' | 'ES'>(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/news/')) return 'EN';
    const match = hash.match(/[?&]lang=(EN|ES)/);
    return match ? match[1] as 'EN' | 'ES' : 'EN';
  });
  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash;
      setShowAdmin(hash === '#/admin');
      setShowConsular(hash === '#/consular');
      const isBusinessMap = hash.startsWith('#/business-map');
      setShowBusinessMap(isBusinessMap);
      if (isBusinessMap) {
        const bmLangMatch = hash.match(/[?&]lang=(EN|ES)/);
        setBusinessMapLang(bmLangMatch ? bmLangMatch[1] as 'EN' | 'ES' : 'EN');
      }
      setShowMembers(hash === '#/members');
      setShowSaveTheDate(hash === '#/save-the-date');
      setShowVoicesNY(hash === '#/voices-of-impact-ny');
      setShowVoicesNetworking(hash === '#/voices-of-impact-ny-networking');
      setShowEncuentroMiami(hash === '#/encuentro-catracho-miami');
      setShowIndependenceDay(hash === '#/independence-day');
      setShowNationalNetwork(hash === '#/national-network');
      const isMember = hash.startsWith('#/member/');
      setShowMember(isMember);
      if (isMember) {
        setMemberSlug(hash.replace('#/member/', ''));
      }
      const isNews = hash.startsWith('#/news/');
      setShowNews(isNews);
      if (isNews) {
        const raw = hash.replace('#/news/', '');
        setNewsPostId(raw.split('?')[0]);
        const langMatch = raw.match(/[?&]lang=(EN|ES)/);
        setNewsLang(langMatch ? langMatch[1] as 'EN' | 'ES' : 'EN');
      }
      // When navigating back to root from a sub-page, skip the landing page
      if (hash === '' || hash === '#') {
        setShowLanding(false);
      }
    };
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);
  const handleReturnToLanding = () => {
    // Language switching keeps the visitor on the current page.
    setShowLanding(false);
  };
  if (showAdmin) {
    return <Suspense fallback={<SectionLoader data-id="element-69" data-mp="1ti6qstng766x" />} data-id="element-68" data-mp="1tej1y7sy77yj">
        <AdminDashboard onBack={() => {
        window.location.hash = '';
        setShowAdmin(false);
      }} data-id="element-70" data-mp="1ogr5r1l9szoj" />
      </Suspense>;
  }
  if (showConsular) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-71" data-mp="2h67xdx12gcjp">
        <Suspense fallback={<SectionLoader data-id="element-73" data-mp="2c6vaxde3x7hn" />} data-id="element-72" data-mp="2h7izztirze98">
          <ConsularPage data-id="element-74" data-mp="2c7mv7s297ism" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showBusinessMap) {
    return <LanguageProvider initialLanguage={businessMapLang} onLanguageSwitch={handleReturnToLanding} data-id="element-75" data-mp="27ek9g63pxdmz">
        <Suspense fallback={<SectionLoader data-id="element-77" data-mp="2zw62q5dhbrjn" />} data-id="element-76" data-mp="23c777ro6jxuy">
          <BusinessMapPage data-id="element-78" data-mp="2zwfu9xe6a6rg" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showMembers) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-79" data-mp="2v4ejyh0keq42">
        <Suspense fallback={<SectionLoader data-id="element-81" data-mp="t7nn10rnlujz" />} data-id="element-80" data-mp="t877op8oux1p">
          <MembersDirectoryPage data-id="element-82" data-mp="t8fi8ahc9q9q" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showVoicesNY) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-83" data-mp="y93b71p4oh6l">
        <Suspense fallback={<SectionLoader data-id="element-85" data-mp="jhude5b2npid" />} data-id="element-84" data-mp="jhcj3e7yzld3">
          <VoicesOfImpactNYPage data-id="element-86" data-mp="oh78fdc3k2w1" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showVoicesNetworking) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-87" data-mp="ah66duk3ipez">
        <Suspense fallback={<SectionLoader data-id="element-89" data-mp="fh22l7mjxcjz" />} data-id="element-88" data-mp="fgivyn2cqal9">
          <VoicesOfImpactNetworkingPage data-id="element-90" data-mp="fhad4sv7c5s0" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showEncuentroMiami) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-91" data-mp="lmerkckecw1">
        <Suspense fallback={<SectionLoader data-id="element-93" data-mp="5il47pla9vnw" />} data-id="element-92" data-mp="5j4h2rw2ttv2">
          <EncuentroCatrachoMiamiPage data-id="element-94" data-mp="1ux9g7ppypble" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showIndependenceDay) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-95" data-mp="1zvb0gdz4jgyg">
        <Suspense fallback={<SectionLoader data-id="element-97" data-mp="3kdmya6p6wgwr" />} data-id="element-96" data-mp="3ke53hstty5gt">
          <HonduranIndependenceDayPage data-id="element-98" data-mp="3key0w36wfima" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showNationalNetwork) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-99" data-mp="3fdh1yd7z668c">
        <Suspense fallback={<SectionLoader data-id="element-101" data-mp="3u6aup0c9ciuy" />} data-id="element-100" data-mp="3u5smd5cbme6o">
          <NationalNetworkPage data-id="element-102" data-mp="3pdredvm6r9mg" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showSaveTheDate) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-103" data-mp="6yqk4sh7k1hr">
        <Suspense fallback={<SectionLoader data-id="element-105" data-mp="2yg6qur368sv" />} data-id="element-104" data-mp="2xwyk5r8mys1">
          <SaveTheDatePage data-id="element-106" data-mp="2x52kttqaz18" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showMember) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-107" data-mp="gwwmtcau9bno">
        <Suspense fallback={<SectionLoader data-id="element-109" data-mp="bz4kdocki758" />} data-id="element-108" data-mp="bzmplah7jvpa">
          <MemberProfilePage slug={memberSlug} data-id="element-110" data-mp="bws55iy72avv" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showNews) {
    return <LanguageProvider initialLanguage={newsLang} data-id="element-111" data-mp="2dqhnc7uz64yn">
        <Suspense fallback={<SectionLoader data-id="element-113" data-mp="2dsawpuwz33ac" />} data-id="element-112" data-mp="2drspy4cop6o6">
          <NewsStoryPage postId={newsPostId} data-id="element-114" data-mp="1rxc1dsws24x3" />
        </Suspense>
      </LanguageProvider>;
  }
  return <>
      <GoogleAnalytics data-id="element-115" data-mp="1wuu0krkzt5rw" />
      <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-116" data-mp="1wu2fiap0uqfv">
        <AppContent showLanding={showLanding} setShowLanding={setShowLanding} data-id="element-117" data-mp="21mc3hl6ktgfi" />
      </LanguageProvider>
    </>;
}